import React, { useState, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import Modal from 'components/Modal'
import Button from 'components/Button'
import Select from 'components/Select'
import { propOr } from 'ramda'
import { update } from 'ramda'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import StudentTeachersList from 'modules/students/components/StudentTeachersList'
import AddStudentTeacherModal from 'modules/students/components/AddStudentTeacherModal'
import { updateStudent } from 'services/StudentsService'
import { updateTeacherAddedStudents } from 'services/TeachersService'
import { isNotNilOrEmpty } from 'utils/ramda'
import { useSelector } from 'react-redux'
import { selectTeachersList } from 'modules/teachers/ducks/selectors'
import { STUDENT_ACCOUNTS } from 'utils/student'

const getSuggestedAccount = (teachers, teachersList) => {
  if (!teachers?.length || !teachersList?.length) return null
  for (const t of teachers) {
    const full = teachersList.find(tl => tl.id === t.id)
    if (full?.account) return full.account
  }
  return null
}

const AssignTeachersModal = ({ student, actionCallback, showAccountSection = false }) => {
  const [open, setOpen] = useState(false)
  const [teachers, setTeachers] = useState([])
  const [selectedAccount, setSelectedAccount] = useState('')
  const teachersList = useSelector(selectTeachersList)

  const suggestedAccount = useMemo(
    () => getSuggestedAccount(teachers, teachersList),
    [teachers, teachersList]
  )

  const handleOpen = e => {
    e.stopPropagation()
    setTeachers(propOr([], 'teachers', student))
    setSelectedAccount(propOr('', 'account', student))
    setOpen(true)
  }

  const handleClose = e => {
    e && e.stopPropagation()
    setOpen(false)
  }

  useEffect(() => {
    if (open && teachers.length > 0) {
      const suggested = getSuggestedAccount(teachers, teachersList)
      setSelectedAccount(suggested ?? propOr('', 'account', student))
    }
  }, [open, teachers, teachersList, student])

  const handleAddTeacher = teacherValues => {
    setTeachers(prev => [...prev, teacherValues])
  }

  const handleEditTeacher = (teacherValues, index) => {
    setTeachers(prev => update(index, teacherValues, prev))
  }

  const handleDeleteTeacher = index => {
    setTeachers(prev => prev.filter((_, i) => i !== index))
  }

  const handleSave = () => {
    const studentPayload = {
      ...student,
      teachers
    }
    if (showAccountSection) {
      studentPayload.account = selectedAccount
    }
    updateStudent({
      student: studentPayload,
      callback: () => {
        const studentTeacherIds = (student.teachers || []).map(t => t.id)
        const newTeacherIds = teachers
          .map(t => t.id)
          .filter(id => !studentTeacherIds.includes(id))
        newTeacherIds.forEach(teacherId => {
          updateTeacherAddedStudents({
            teacherId,
            student: { ...student, teachers }
          })
        })
        handleClose()
        typeof actionCallback === 'function' && actionCallback()
      }
    })
  }

  const accountOptions = [
    { value: '', label: '—' },
    ...Object.entries(STUDENT_ACCOUNTS).map(([value, label]) => ({ value, label }))
  ]

  return (
    <>
      <Trigger onClick={handleOpen} title='Przypisz nauczycieli'>
        <PersonAddIcon />
      </Trigger>
      <Modal
        open={open}
        onClose={handleClose}
        title='Przypisz nauczycieli do ucznia'
      >
        <Content>
          <SectionHeader>Przypisani nauczyciele</SectionHeader>
          <AddStudentTeacherModal onAddTeacher={handleAddTeacher} />
          {isNotNilOrEmpty(teachers) && (
            <StudentTeachersList
              teachers={teachers}
              onEditTeacher={handleEditTeacher}
              onDeleteTeacher={handleDeleteTeacher}
            />
          )}
          {showAccountSection && isNotNilOrEmpty(teachers) && (
            <AccountSection>
              <SectionHeader>Konto do wpłat ucznia</SectionHeader>
              {suggestedAccount && (
                <SuggestionText>
                  Sugerowane konto to: <strong>{STUDENT_ACCOUNTS[suggestedAccount] ?? suggestedAccount}</strong>
                </SuggestionText>
              )}
              <Select
                label='Konto do wpłat ucznia'
                name='account'
                value={selectedAccount}
                onChange={(_, value) => setSelectedAccount(value ?? '')}
                options={accountOptions}
              />
            </AccountSection>
          )}
          <ButtonsWrapper>
            <Button onClick={handleClose} color='secondary'>
              Anuluj
            </Button>
            <Button onClick={handleSave} color='primary'>
              Zapisz
            </Button>
          </ButtonsWrapper>
        </Content>
      </Modal>
    </>
  )
}

export default AssignTeachersModal

const Trigger = styled.div`
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.darkGrey};

  &:hover {
    color: ${({ theme }) => theme.colors.secondary.main};
  }
`

const Content = styled.div`
  padding-top: 10px;
`

const SectionHeader = styled.div`
  font-size: 14px;
  text-transform: uppercase;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.colors.secondary.main};
`

const AccountSection = styled.div`
  margin-top: 20px;
  padding-top: 16px;

  strong {
    font-weight: bold;
    color: ${({ theme }) => theme.colors.primary.main};
  }
`

const SuggestionText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.darkGrey};
  margin-bottom: 12px;
  font-size: 16px;
`

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 24px;

  button {
    max-width: 200px;
  }
`
